import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { CustodyActionType } from '../../../types/custody-actions'
import { useCustodyActions } from '../../../hooks/use-custody-actions'
import { useEvidenceSummary } from '../../../hooks/use-evidence-numbers'
import { CustodyActionHistory } from './CustodyActionHistory'
import { ReceiveEvidenceForm } from './ReceiveEvidenceForm'
import { StoreEvidenceForm } from './StoreEvidenceForm'
import { RelocateEvidenceForm } from './RelocateEvidenceForm'
import { ReturnEvidenceForm } from './ReturnEvidenceForm'
import { DestroyEvidenceForm } from './DestroyEvidenceForm'

interface CustodyActionsTabProps {
  caseId: string
}

type ActionTitleKey =
  | 'custodyActions.receive'
  | 'custodyActions.store'
  | 'custodyActions.relocate'
  | 'custodyActions.return'
  | 'custodyActions.destroy'
type ActionDescKey =
  | 'custodyActions.receiveDesc'
  | 'custodyActions.storeDesc'
  | 'custodyActions.relocateDesc'
  | 'custodyActions.returnDesc'
  | 'custodyActions.destroyDesc'

interface ActionCard {
  type: CustodyActionType
  titleKey: ActionTitleKey
  descKey: ActionDescKey
  colourClass: string
}

const ACTION_CARDS: ActionCard[] = [
  {
    type: 'receive',
    titleKey: 'custodyActions.receive',
    descKey: 'custodyActions.receiveDesc',
    colourClass: 'border-sky-300 dark:border-sky-700',
  },
  {
    type: 'store',
    titleKey: 'custodyActions.store',
    descKey: 'custodyActions.storeDesc',
    colourClass: 'border-slate-300 dark:border-slate-600',
  },
  {
    type: 'relocate',
    titleKey: 'custodyActions.relocate',
    descKey: 'custodyActions.relocateDesc',
    colourClass: 'border-amber-300 dark:border-amber-700',
  },
  {
    type: 'return',
    titleKey: 'custodyActions.return',
    descKey: 'custodyActions.returnDesc',
    colourClass: 'border-indigo-300 dark:border-indigo-700',
  },
  {
    type: 'destroy',
    titleKey: 'custodyActions.destroy',
    descKey: 'custodyActions.destroyDesc',
    colourClass: 'border-red-300 dark:border-red-700',
  },
]

interface ActiveFormProps {
  activeForm: CustodyActionType | null
  caseId: string
  onClose: () => void
  onSuccess: () => void
}

function ActiveFormModal({
  activeForm,
  caseId,
  onClose,
  onSuccess,
}: ActiveFormProps): React.ReactElement {
  return (
    <>
      {activeForm === 'receive' && (
        <ReceiveEvidenceForm
          caseId={caseId}
          open={true}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
      {activeForm === 'store' && (
        <StoreEvidenceForm
          caseId={caseId}
          open={true}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
      {activeForm === 'relocate' && (
        <RelocateEvidenceForm
          caseId={caseId}
          open={true}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
      {activeForm === 'return' && (
        <ReturnEvidenceForm
          caseId={caseId}
          open={true}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
      {activeForm === 'destroy' && (
        <DestroyEvidenceForm
          caseId={caseId}
          open={true}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </>
  )
}

interface ActionCardButtonProps {
  card: ActionCard
  onClick: (type: CustodyActionType) => void
  disabled?: boolean
  disabledReason?: string
}

function ActionCardButton({
  card,
  onClick,
  disabled,
  disabledReason,
}: ActionCardButtonProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <button
      type="button"
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      onClick={() => {
        onClick(card.type)
      }}
      className={`flex flex-col gap-1 rounded-lg border-2 bg-[var(--bg-card)] p-4 text-left transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:opacity-40 ${card.colourClass}`}
    >
      <span className="text-sm font-[var(--font-mono)] font-medium tracking-wider text-[var(--text-primary)] uppercase">
        {t(card.titleKey)}
      </span>
      <span className="text-xs text-[var(--text-muted)]">{t(card.descKey)}</span>
    </button>
  )
}

export function CustodyActionsTab({ caseId }: CustodyActionsTabProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  const [activeForm, setActiveForm] = useState<CustodyActionType | null>(null)
  const { refetch } = useCustodyActions(caseId)
  const { summary, refetch: refetchSummary } = useEvidenceSummary(caseId)
  const hasCustody = (summary?.inCustodyCount ?? 0) > 0

  const handleOpen = useCallback((type: CustodyActionType) => {
    setActiveForm(type)
  }, [])
  const handleClose = useCallback(() => {
    setActiveForm(null)
  }, [])

  const handleSuccess = useCallback(() => {
    setActiveForm(null)
    void refetch()
    void refetchSummary()
  }, [refetch, refetchSummary])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {ACTION_CARDS.map((card) => {
          const requiresCustody = card.type !== 'receive'
          const isDisabled = requiresCustody && !hasCustody
          return (
            <ActionCardButton
              key={card.type}
              card={card}
              onClick={handleOpen}
              disabled={isDisabled}
              {...(isDisabled
                ? {
                    disabledReason: t('custodyActions.noEvidenceInCustody', {
                      defaultValue: 'No evidence currently in custody. Receive evidence first.',
                    }),
                  }
                : {})}
            />
          )
        })}
      </div>
      <CustodyActionHistory caseId={caseId} />
      <ActiveFormModal
        activeForm={activeForm}
        caseId={caseId}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    </div>
  )
}
